import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  approveVolunteer,
  buildVolunteerPlanningCsvUrl,
  clearVolunteerStaffAccess,
  createAdminVolunteer,
  deleteOrRejectVolunteer,
  extractApiError,
  fetchAdminVolunteers,
  fetchVolunteerConflicts,
  renewAllVolunteers,
  renewVolunteer,
  sendVolunteerLink,
  setVolunteerStaffAccess,
} from '../api.js';
import {
  STORAGE_KEYS,
  getAdminAuthHeaders,
  getVolunteerMeta,
  getVolunteerSession,
} from '../useActiveRoles.js';

function authHeaders() {
  const h = getAdminAuthHeaders();
  if (Object.keys(h).length) return h;
  const session = getVolunteerSession();
  return session ? { 'X-Volunteer-Session': session } : {};
}

/** Création / validation / renew / droits site : super-admin ou jeton ADMIN. */
function canManageRegistry() {
  if (typeof window === 'undefined') return false;
  if ((localStorage.getItem(STORAGE_KEYS.admin) || '').trim()) return true;
  return Boolean(getVolunteerMeta()?.is_super_admin);
}

function formatAccess(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const expired = d.getTime() < Date.now();
  return `${d.toLocaleDateString('fr-FR')}${expired ? ' (expiré)' : ''}`;
}

/**
 * Panneau super-admin : file pending, renew, accès site, conflits, export CSV.
 * Utilisable depuis /admin (jeton) ou /benevoles/planning (session super-admin).
 */
export default function AdminVolunteersPanel({ embedded = false }) {
  const [list, setList] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    last_name: '',
    first_name: '',
    email: '',
    phone: '',
  });
  const [creating, setCreating] = useState(false);
  const manageRegistry = canManageRegistry();

  const refresh = useCallback(async () => {
    const headers = authHeaders();
    if (!Object.keys(headers).length) {
      setError('Authentification admin requise.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [vols, conf] = await Promise.all([
        fetchAdminVolunteers(headers),
        fetchVolunteerConflicts(headers),
      ]);
      setList(vols);
      setConflicts(conf);
    } catch (err) {
      setError(extractApiError(err, 'Chargement bénévoles impossible.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function run(action, okMsg) {
    setError(null);
    setInfo(null);
    try {
      await action();
      if (okMsg) setInfo(okMsg);
      await refresh();
    } catch (err) {
      setError(extractApiError(err, 'Action impossible.'));
    }
  }

  async function downloadCsv() {
    const headers = authHeaders();
    try {
      const res = await fetch(buildVolunteerPlanningCsvUrl(), { headers });
      if (!res.ok) throw new Error('Export impossible');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'planning-benevoles.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractApiError(err, 'Export CSV impossible.'));
    }
  }

  async function submitCreate(e) {
    e.preventDefault();
    const last_name = form.last_name.trim();
    const first_name = form.first_name.trim();
    if (!last_name || !first_name) {
      setError('Nom et prénom obligatoires.');
      return;
    }
    setCreating(true);
    setError(null);
    setInfo(null);
    try {
      const email = form.email.trim();
      await createAdminVolunteer(authHeaders(), {
        last_name,
        first_name,
        email: email || null,
        phone: form.phone.trim() || null,
        activate: true,
      });
      setForm({ last_name: '', first_name: '', email: '', phone: '' });
      setInfo(
        email
          ? 'Bénévole créé et activé — lien magique envoyé par email.'
          : 'Bénévole créé sans email (actif). Affectez-le ensuite sur le planning.',
      );
      await refresh();
    } catch (err) {
      setError(extractApiError(err, 'Création impossible.'));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{ p: embedded ? 0 : 2, border: embedded ? 0 : 1, borderColor: 'divider' }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Bénévoles</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" onClick={refresh} disabled={loading}>
            Actualiser
          </Button>
          {manageRegistry && (
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                run(
                  () => renewAllVolunteers(authHeaders()),
                  'Tous les accès actifs ont été prolongés de 30 jours (sans mail).',
                )
              }
            >
              Renew all +30j
            </Button>
          )}
          <Button size="small" startIcon={<DownloadIcon />} onClick={downloadCsv}>
            Export planning CSV
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {conflicts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {conflicts.length} chevauchement(s) d&apos;inscriptions détecté(s).
          <Stack component="ul" sx={{ m: 0, pl: 2 }}>
            {conflicts.slice(0, 8).map((c, i) => (
              <li key={i}>
                {c.first_name} {c.last_name} : {c.shift_a?.title} ↔ {c.shift_b?.title}
              </li>
            ))}
          </Stack>
        </Alert>
      )}

      {manageRegistry ? (
        <Paper
          component="form"
          onSubmit={submitCreate}
          variant="outlined"
          sx={{ p: 2, mb: 2 }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Ajouter un bénévole manuellement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Laissez l&apos;email vide s&apos;il n&apos;en a pas — le compte sera actif tout de suite
            (pas de lien de connexion). Affectez ensuite ses créneaux sur le planning.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1 }}
          >
            <TextField
              required
              size="small"
              label="Nom"
              value={form.last_name}
              onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
              sx={{ minWidth: 140 }}
            />
            <TextField
              required
              size="small"
              label="Prénom"
              value={form.first_name}
              onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              label="Email (optionnel)"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              label="Téléphone"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              sx={{ minWidth: 140 }}
            />
            <Button type="submit" variant="contained" disabled={creating}>
              {creating ? 'Création…' : 'Créer actif'}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Pour affecter des bénévoles aux créneaux, utilisez le planning
          (/benevoles/planning). La création et la validation des comptes restent
          réservées au super-admin.
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nom</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Accès</TableCell>
            <TableCell>Site</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((v) => (
            <TableRow key={v.id} hover>
              <TableCell>
                {v.last_name} {v.first_name}
              </TableCell>
              <TableCell>{v.email || '—'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={v.status}
                  color={
                    v.status === 'pending'
                      ? 'warning'
                      : v.status === 'active'
                        ? 'success'
                        : 'default'
                  }
                />
              </TableCell>
              <TableCell>{formatAccess(v.access_expires_at)}</TableCell>
              <TableCell>{v.staff_role || '—'}</TableCell>
              <TableCell align="right">
                {manageRegistry ? (
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                    {v.status === 'pending' && (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            run(
                              () => approveVolunteer(authHeaders(), v.id),
                              v.email
                                ? 'Validé — mail lien magique envoyé.'
                                : 'Validé (sans email, pas de mail).',
                            )
                          }
                        >
                          Valider
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            run(
                              () => deleteOrRejectVolunteer(authHeaders(), v.id),
                              'Demande supprimée.',
                            )
                          }
                        >
                          Refuser
                        </Button>
                      </>
                    )}
                    {v.status === 'active' && (
                      <>
                        {v.email && (
                          <Button
                            size="small"
                            onClick={() =>
                              run(
                                () => sendVolunteerLink(authHeaders(), v.id),
                                'Nouveau lien envoyé par email.',
                              )
                            }
                          >
                            Renvoyer le lien
                          </Button>
                        )}
                        <Button
                          size="small"
                          onClick={() =>
                            run(
                              () => renewVolunteer(authHeaders(), v.id),
                              'Accès prolongé (sans mail).',
                            )
                          }
                        >
                          +30j
                        </Button>
                        <TextField
                          select
                          size="small"
                          label="Site"
                          value={v.staff_role || ''}
                          sx={{ minWidth: 110 }}
                          onChange={(e) => {
                            const role = e.target.value;
                            if (!role) {
                              run(() => clearVolunteerStaffAccess(authHeaders(), v.id));
                            } else {
                              run(() => setVolunteerStaffAccess(authHeaders(), v.id, role));
                            }
                          }}
                        >
                          <MenuItem value="">Aucun</MenuItem>
                          <MenuItem value="saisie">Caisse</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </TextField>
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            run(
                              () => deleteOrRejectVolunteer(authHeaders(), v.id),
                              'Compte passé en inactive.',
                            )
                          }
                        >
                          Révoquer
                        </Button>
                      </>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!list.length && !loading && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary">
                  Aucun bénévole pour l’instant.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}
